import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS, SERVICES, INDUSTRIES } from "@/data/content";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDrop(null);
  }, [pathname]);

  const solid = scrolled || mobileOpen || pathname === "/book-consultation";
  const textColor = solid ? "text-slate-700" : "text-white/90";
  const linkHover = solid ? "hover:text-[#1E40AF]" : "hover:text-white";

  const dropItems = { services: SERVICES, industries: INDUSTRIES };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        solid
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-[0_1px_20px_rgba(15,23,42,0.05)]"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8">
        <div className="h-[76px] flex items-center justify-between gap-4">
          <Logo light={!solid} />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((link) =>
              link.dropdown ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setOpenDrop(link.dropdown)}
                  onMouseLeave={() => setOpenDrop(null)}
                >
                  <Link
                    to={link.to}
                    data-testid={`nav-${link.dropdown}`}
                    className={`flex items-center gap-1 px-4 py-2 text-[15px] font-medium transition-colors duration-200 ${textColor} ${linkHover}`}
                  >
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${openDrop === link.dropdown ? "rotate-180" : ""}`} />
                  </Link>
                  <AnimatePresence>
                    {openDrop === link.dropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                      >
                        <div className={`${link.dropdown === "industries" ? "w-[520px] grid grid-cols-2" : "w-[340px] grid grid-cols-1"} gap-1 rounded-2xl bg-white border border-slate-200 shadow-xl shadow-slate-900/10 p-2`}>
                          {dropItems[link.dropdown].map((item) => (
                            <Link
                              key={item.slug || item.name}
                              to={link.dropdown === "services" ? `/services#${item.slug}` : `/industries#${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                              data-testid={`dropdown-${(item.slug || item.name).toString().toLowerCase().replace(/\s+/g, "-")}`}
                              className="flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-slate-50 transition-colors group"
                            >
                              <span className="mt-0.5 flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-[#1E40AF] group-hover:bg-[#1E40AF] group-hover:text-white transition-colors">
                                <item.icon className="w-4 h-4" strokeWidth={1.75} />
                              </span>
                              <span className="text-sm font-medium text-slate-700 leading-snug group-hover:text-[#1E40AF]">
                                {item.title || item.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={link.label}
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-2 text-[15px] font-medium transition-colors duration-200 ${textColor} ${linkHover} ${
                      isActive ? (solid ? "!text-[#1E40AF]" : "!text-white") : ""
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/book-consultation"
              data-testid="header-cta"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#1E40AF] hover:bg-[#2563EB] text-white text-sm font-semibold px-5 py-2.5 transition-colors duration-200 shadow-sm hover:shadow-md"
            >
              Book a Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${solid ? "text-slate-800 hover:bg-slate-100" : "text-white hover:bg-white/10"}`}
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-1 max-h-[75vh] overflow-y-auto">
              {NAV_LINKS.map((link) => (
                <div key={link.label}>
                  <Link
                    to={link.to}
                    className="block px-3 py-3 text-base font-semibold text-slate-800 rounded-lg hover:bg-slate-50"
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.label}
                  </Link>
                  {link.dropdown && (
                    <div className="pl-4 border-l border-slate-200 ml-3 mb-2">
                      {dropItems[link.dropdown].map((item) => (
                        <Link
                          key={item.slug || item.name}
                          to={link.dropdown === "services" ? `/services#${item.slug}` : `/industries#${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                          className="block px-3 py-2 text-sm text-slate-600 rounded-lg hover:bg-slate-50"
                        >
                          {item.title || item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link
                to="/book-consultation"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#1E40AF] text-white text-sm font-semibold px-5 py-3"
                data-testid="mobile-header-cta"
              >
                Book a Consultation <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
